import { TABLE, TABLE_CELL, TABLE_HEAD, TableCard } from './TableCard';
import { useEffect, useMemo, useState } from 'react';
import { Check, Drill, Loader2, Nut } from 'lucide-react';
import { PickAllButton, PLAIN } from './PickAllButton';
import { useAssemblySteps } from './categoryBands';
import { callError } from './callError';
import { categoriesLoaded, useCategories } from './catalogCategories';
import { clearDrill, setHoleMarks, type HoleMark } from './drillPreview';
import { faceKey, type Face } from './faces';
import { useHoleFit } from './holeFit';
import { huesByVia, SAID_COLOURS, tint } from './holeColours';
import { builtBoxOf } from './openaiBlenderApi';
import { mmScale } from './meshAnnotations';
import { boredSpots } from './modelsStages';
import { useModelSizes } from './partModelSize';
import { isLeftRight, usePartsRun } from './partsRun';
import { usePartSide } from './partSide';
import { meshChanged, useShownMesh } from './shownMesh';
import { usePartLabel } from './SubStepChips';
import { holePlace, holeWidthOf, isCentred, makesOwnHole, shownHoleId, type StepHole } from './stepHoles';
import { boreOf, boreRequest, holeDepthOf, sameBore, type Bore } from './bores';
import { useStepHoles } from './stepHolesRun';
import { drillJob, isTrueScale } from './threeDApi';
import { useLanguage } from '../../contexts/LanguageContext';
import { useStableText } from '../../components/StableText';
import { useHoleSubStep } from './assemblyClip';

// ONE BORE PER ROW, resolved against the mesh on screen — see bores.ts, which the Holes & covers
// run's batch reads too, so the two cut identically.
type HoleCut = Bore;

// THE HOLES FIND HOLES FOUND, ONE ROW EACH — under the mesh they are bored in.
//
// It was a column of Phase 4's sub-step table: a card per GESTURE, six lines tall, so one bore
// worked in twice was two cards and a part's holes were scattered down forty rows. Here a hole
// is a row, its part beside it and every sub-step that touches it in one cell — the drilling
// question is "what does this plank need", and that reads down a column sorted by plank.
//
// AND IT IS THE PANEL THAT DRILLS THEM. It stood beside a second table of the same rows inside
// the expanded viewer, which had the buttons: the reading was on the page and the work was two
// clicks away, under a picture covering it. The mesh it acts on is the one in the card above
// (see shownMesh), so pressing a row's Drill cuts what is on screen.
// A FACE, AND A PLACE ON IT, in words — the Face and Position columns, here and in the covers table.
export function useHoleWords() {
  const { t } = useLanguage();
  const st = useStableText();
  const faceWord = (f: Face) => t(faceKey(f));
  const axis = (at: StepHole['u']) => (isCentred(at)
    ? st('notice.holes.centred')
    : st('notice.holes.fromEdge', (s) => s.replace('{mm}', String(at.mm)).replace('{edge}', faceWord(at.from))));
  return { faceWord, axis };
}

export function HolesTable({ docId, only, locked }: {
  docId: string;
  // WHICH PART'S HOLES, as its index in the parts list — the part whose mesh is on screen, this
  // table standing under it. Every hole in the manual where the caller has no part open.
  only?: number;
  // Nothing is cut in the demo. The run buttons of every phase are dead there, and a drill is
  // a Blender on the server.
  locked?: boolean;
}) {
  const { t, language } = useLanguage();
  const st = useStableText();
  const { steps } = useAssemblySteps(docId);
  const { items } = usePartsRun(docId);
  const holes = useStepHoles(docId);
  const holeSubStep = useHoleSubStep(docId);
  const models = useModelSizes(docId).byPart;
  const label = usePartLabel(items);
  // The ontology the hole styles are read out of. It arrives from the server a moment after
  // this table does, and a memo that did not depend on it would answer once, before it landed,
  // and never again. See holeStyleOf, which answers null until it is here.
  const cats = useCategories();
  const { faceWord, axis } = useHoleWords();

  // THE MESH IN THE CARD ABOVE, and the millimetres it is read at. Null where no part is open
  // or the viewer has not measured one yet — which is what greys the drill rather than letting
  // a bore be placed against a box nobody measured.
  const mesh = useShownMesh();
  const job = mesh?.job ?? null;
  const profile = mesh?.profile ?? null;
  const mmPerUnit = mesh?.size
    ? mmScale(mesh.size, mesh.dims, isTrueScale(job), builtBoxOf(job))
    : null;
  const meshDims = useMemo(
    () => (mesh?.size && mmPerUnit
      ? { x: mesh.size.x * mmPerUnit, y: mesh.size.y * mmPerUnit, z: mesh.size.z * mmPerUnit }
      : null),
    [mesh?.size, mmPerUnit],
  );
  // WHICH BORES ARE ALREADY IN THIS MESH, read off the run's own edit history rather than
  // measured: a drill is not idempotent, and the run knows what it cut. See boredSpots.
  const alreadyBored = useMemo(() => boredSpots(job), [job]);
  // AND WHICH HAND IS ON SCREEN. The file is the LEFT one and the right is it reflected, so a
  // bore cut while the right is up would go in at the left one's place. Refused with the
  // reason rather than cut quietly.
  const shownSide = usePartSide();
  const wrongHand = only != null && isLeftRight(items[only]) && shownSide === 'right';

  // EVERY HOLE, with where it is drilled and every later gesture that works in it again.
  const rows = useMemo(() => steps.flatMap((step) => {
    const found = holes.byStep[step.category];
    if (!found) return [];
    return found.holes.map((hole) => ({
      hole,
      // As shown now, where Phase 4 has merged the one it names away (see useHoleSubStep).
      at: [...new Set([
        // NONE FOR A HOLE NO GESTURE CLAIMS (see relinkHoles): it is timed by no sub-step.
        ...(hole.at ? [holeSubStep(step.num, hole.at, hole.via)] : []),
        ...Object.entries(found.fills)
          .filter(([letter, ids]) => ids.includes(hole.id) && letter !== hole.at)
          .map(([letter]) => holeSubStep(step.num, letter, hole.via)),
      ])],
    }));
  }).filter(({ hole }) => only == null || hole.part === String(only))
    .sort((a, b) => Number(a.hole.part) - Number(b.hole.part)
    || a.hole.id.localeCompare(b.hole.id, undefined, { numeric: true })),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [steps, holes.byStep, only, holeSubStep]);

  const widthOf = (h: StepHole): number | null => holeWidthOf(h, items, models);
  // HOW DEEP A BORE GOES, AND WHETHER ANYBODY SAID SO. Phase 4 is allowed to answer null, and
  // on a real manual it mostly does: a drawing that dimensions a hole's position rarely
  // dimensions its depth. The FASTENER answers where the drawing does not — a 40 mm dowel
  // needs 40 mm of hole — which is weaker than a stated depth and is the one number in the
  // room that is about this bore. Never written back into the answer.
  const holeDepth = (h: StepHole) => holeDepthOf(h, models);

  // ONE COLOUR PER PIECE OF HARDWARE, not per row: a rail's two barrel nuts, then the two
  // screws through them, are three colours that say which bore takes which fastener. Coloured
  // per row they were six unrelated hues, and the pairing had to be read out of the Takes
  // column every time.
  const holeHue = useMemo(() => huesByVia(rows.map(({ hole }) => hole)), [rows]);

  // WHICH OF THE LISTED HOLES CAN ACTUALLY BE CUT, and each one only once.
  //
  // A bore needs three things the drill will not do without: a place this part's extents can
  // resolve, a width, and a depth (or that it goes through). Anything short of that is left out
  // and counted by the reason, rather than sent to be refused one at a time — and the reasons
  // are not the same kind of thing: a bore with no width is an answer missing a field, one with
  // nowhere to go is a PART nobody has measured, which is fixed a panel away.
  const cuts = useMemo(() => {
    const out = new Map<string, HoleCut>();
    const why: Record<'wood' | 'place' | 'width' | 'depth' | 'done', string[]> = {
      wood: [], place: [], width: [], depth: [], done: [],
    };
    rows.forEach(({ hole }) => {
      // NOTHING TO CUT FOR A POINTED SCREW (see makesOwnHole): its row says so, below.
      if (hole.via != null && makesOwnHole(items[Number(hole.via)])) return;
      const bore = boreOf(hole, meshDims, profile, items, models);
      // NOT WHILE THE SHAPE IS UNKNOWN (null): see boreOf.
      if (!bore) return;
      if (typeof bore === 'string') { why[bore].push(hole.id); return; }
      if (alreadyBored.some((p) => sameBore(p, { face: hole.face, u: bore.u, v: bore.v, diameter: bore.diameter }))) {
        why.done.push(hole.id); return;
      }
      out.set(hole.id, bore);
    });
    return Object.assign(out, { why });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, meshDims, profile, items, models, alreadyBored, cats]);

  // AND THE BATCH: those, minus the ones that land where an earlier one already has. The dedup
  // is a fact about cutting fourteen holes in one press and not about the hole — a row left out
  // because a sibling makes the same bore is still a perfectly good bore, and its own Drill
  // button cuts it.
  const drillable = useMemo(() => {
    const out: HoleCut[] = [];
    const twice: string[] = [];
    rows.forEach(({ hole }) => {
      const k = cuts.get(hole.id);
      if (!k) return;
      const same = out.some((p) => p.hole.face === k.hole.face
        && Math.abs(p.u - k.u) < 0.5 && Math.abs(p.v - k.v) < 0.5
        && p.diameter === k.diameter);
      if (same) { twice.push(hole.id); return; }
      out.push(k);
    });
    return Object.assign(out, { why: { ...cuts.why, twice } });
  }, [rows, cuts]);

  // WHAT IT WILL NOT CUT AND WHY, one line each, naming the rows it is about — "No depth:
  // holes H103, H105 and H107" is the same fact as a tally and points at them.
  const skipped = useMemo(() => {
    const { why } = drillable;
    const list = (ns: string[]) => {
      const said = ns.length > 1
        ? `${ns.slice(0, -1).join(', ')} ${t('notice.3d.skipAnd')} ${ns[ns.length - 1]}`
        : ns[0];
      return t(ns.length > 1 ? 'notice.3d.skipMany' : 'notice.3d.skipOne').replace('{n}', said);
    };
    // Said once and not per row: every hole is waiting on the same fetch, and fourteen
    // identical lines would read as fourteen faults.
    const waiting = categoriesLoaded() ? [] : [t('notice.3d.skipOntology')];
    return waiting.concat(([
      ['notice.3d.skipWood', why.wood],
      ['notice.3d.skipPlace', why.place],
      ['notice.3d.skipWidth', why.width],
      ['notice.3d.skipDepth', why.depth],
      ['notice.3d.skipDone', why.done],
      ['notice.3d.skipTwice', why.twice],
    ] as const)
      .filter(([, ns]) => ns.length > 0)
      .map(([key, ns]) => `${t(key)}: ${list([...ns])}`));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drillable, language]);

  // WHICH ROWS ARE ON THE PART, by hole id — the row is the switch, and any number of them
  // down at once. By id and not by index because the id is the one address a hole has that no
  // ordering touches.
  const [marked, setMarked] = useState<Set<string>>(new Set());
  const [fitShown, setFitShown] = useState<Set<string>>(new Set());
  const flip = (set: (f: (was: Set<string>) => Set<string>) => void, id: string) => set((was) => {
    // OFF THE SET AS IT IS WHEN THE PRESS LANDS, and not as it was when this render was drawn:
    // two presses inside one tick would both read the same captured value.
    const next = new Set(was);
    if (!next.delete(id)) next.add(id);
    return next;
  });
  // AND THE HARDWARE IN THEM, per row: the part the hole is for, slid in on the viewer (see
  // holeFit). Its own set, because looking at a bore and looking at what goes in it are two
  // switches — and a row showing its hardware draws the hardware in place of the bore.
  const fitOf = useHoleFit(docId);

  // WHICH ROWS CAN BE PUT ON THE PART AT ALL — the same test each row's own press makes, so the
  // button below and the rows under it cannot disagree about what "all" means.
  const placeable = useMemo(
    () => rows.filter(({ hole }) => !!holePlace(hole, meshDims, profile)).map(({ hole }) => hole.id),
    [rows, meshDims, profile],
  );
  const allMarked = placeable.length > 0 && placeable.every((id) => marked.has(id));


  // AND WHAT THE VIEWPORT DRAWS, DERIVED FROM THE SET rather than pushed at each press: the
  // marks are not a second piece of state, they are the rows restated as geometry. Pushed
  // instead, every other path that touches the selection — a new mesh, a part closed, a row
  // that stopped being placeable — had to remember to push too.
  //
  // A WIDTH OF ZERO WHERE NOBODY GAVE ONE, which the mark already has a drawing for: a bare
  // cross at the place. Where a bore is is answerable without how wide it is.
  const marks = useMemo<HoleMark[]>(
    () => rows.flatMap(({ hole }) => {
      const fitting = fitShown.has(hole.id);
      if (!marked.has(hole.id) && !fitting) return [];
      const at = holePlace(hole, meshDims, profile);
      if (!at) return [];
      const fit = fitting ? fitOf(hole) : null;
      return [{
        ...(fit ? { fit } : {}),
        face: hole.face as Face,
        u: at.u,
        v: at.v,
        diameter: widthOf(hole) ?? 0,
        depth: hole.through ? null : holeDepth(hole).mm,
        through: hole.through,
        colour: holeHue.get(hole.id) ?? SAID_COLOURS[0],
      }];
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, marked, fitShown, fitOf, meshDims, profile, holeHue],
  );
  useEffect(() => { setHoleMarks(marks); }, [marks]);
  // Nothing of this table's left on the part once it is gone — a mark nothing on the page
  // accounts for is worse than none.
  useEffect(() => () => clearDrill(), []);
  // AND NOTHING CARRIED FROM ONE PART TO THE NEXT. The ids are the manual's, so a selection
  // made on one plank would light rows of another that happen to be numbered the same.
  useEffect(() => { setMarked(new Set()); setFitShown(new Set()); }, [only]);

  // Boring them, one after another: each drill rewrites the mesh the next one cuts into.
  const [boring, setBoring] = useState<{ done: number; total: number } | null>(null);
  const [boringOne, setBoringOne] = useState<string | null>(null);
  const [problem, setProblem] = useState('');
  const cutting = !!boring || boringOne != null;
  const canCut = !!job && !!mmPerUnit && !cutting && !wrongHand && !locked;

  const cutHole = async (k: HoleCut, scale: number) => {
    if (!job) return;
    meshChanged(await drillJob(job.id, { ...boreRequest(k, items), mmPerUnit: scale }));
  };

  // ONE ROW, ON ITS OWN. The list is a reading of what the manual says and a reading can be
  // wrong in one place: cutting the thirteen that are right and arguing with the fourteenth
  // needed the whole batch or the drill form typed out by hand.
  const drillRow = async (k: HoleCut) => {
    if (!canCut || !mmPerUnit) return;
    setProblem('');
    setBoringOne(k.hole.id);
    try {
      await cutHole(k, mmPerUnit);
    } catch (e) {
      setProblem(callError(e, t));
    } finally {
      setBoringOne(null);
    }
  };

  const drillAll = async () => {
    if (!canCut || !mmPerUnit || !drillable.length) return;
    setProblem('');
    setBoring({ done: 0, total: drillable.length });
    try {
      for (let i = 0; i < drillable.length; i += 1) {
        // ONE SCALE FOR THE WHOLE BATCH, taken before the first cut: drilling does not move the
        // bounding box, so re-reading it per hole could only pick up a viewer measurement
        // landing mid-run and bore the rest against a different anchor than the first.
        // eslint-disable-next-line no-await-in-loop
        await cutHole(drillable[i], mmPerUnit);
        setBoring({ done: i + 1, total: drillable.length });
      }
    } catch (e) {
      // STOPPED WHERE IT FAILED, and what was cut before it stays cut: each drill is its own
      // edit on the mesh's history, and a part with the holes it got is a better place to be
      // than one that lost the nine that worked because the tenth did not.
      setProblem(callError(e, t));
    } finally {
      setBoring(null);
    }
  };

  // WHILE THE HOLES ARE BEING LOOKED FOR, the last run's table is about to be replaced, gesture
  // by gesture — shown meanwhile, it reads as the answer to the run that is still out.
  if (holes.busy) {
    return (
      <div className="flex justify-center py-6">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }
  if (!rows.length) return null;
  const th = TABLE_HEAD;
  const td = TABLE_CELL;
  // ITS OWN COLOUR, LIKE EVERY CELL AROUND IT. These carry no text colour of their own at
  // first, so they took the phase's, which is a muted grey — and a row of controls in the
  // colour a page uses for captions reads as a row that has been disabled.
  return (
    <TableCard title={t('notice.holesTable.title')} className="max-w-5xl">
      {/* ONE SET OF HOLES FOR THE PAIR, SAID ONCE AND OVER THE WHOLE TABLE. There is one file,
          it IS the left-hand part, every bore is written in that part's frame, and the right
          one is the same file drawn reflected — so the table is the same table on both hands
          and only the drill knows the difference. Which hand is on screen changes the sentence
          and nothing else, because standing on the right one is exactly when "these are the
          left-hand part's" needs saying. */}
      {only != null && isLeftRight(items[only]) && (
        <p className="mb-2 text-center text-[11px] leading-snug text-slate-500">
          {t(shownSide === 'right' ? 'notice.3d.holesPairRight' : 'notice.3d.holesPairLeft')}
        </p>
      )}
      <div className="w-full overflow-x-auto">
      <table className={TABLE}>
        <thead>
          <tr>
            <th className={th}>{st('notice.holesTable.hole')}</th>
            {/* NOT WHERE ONE PART IS OPEN: its name on every row of its own table is a column
                of the same words under the mesh it is about. */}
            {only == null && <th className={th}>{st('notice.holesTable.part')}</th>}
            <th className={`${th} whitespace-nowrap`}>{st('notice.hardwareTable.colSubsteps')}</th>
            <th className={th}>{st('notice.holesTable.face')}</th>
            <th className={th}>{st('notice.holesTable.position')}</th>
            <th className={th}>{st('notice.holesTable.size')}</th>
            <th className={th}>{st('notice.holesTable.via')}</th>
            {/* THE TWO THINGS DONE TO A BORE, where the bore is. Only under a mesh: with no
                part open there is nothing to show hardware on and nothing to drill. */}
            {only != null && <th className={th}>{st('notice.3d.saidColDrill')}</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map(({ hole: h, at }) => {
            const width = widthOf(h);
            const depth = h.through ? t('notice.holes.through') : h.depthMm != null ? `${h.depthMm} mm` : null;
            const onPart = !!items[Number(h.part)];
            const place = holePlace(h, meshDims, profile);
            const cut = cuts.get(h.id);
            const done = cuts.why.done.includes(h.id);
            const on = marked.has(h.id);
            const switchable = !!place;
            // THE ROW IS THE SWITCH. Pressing it puts the bore on the part in the colour the
            // row wears, and pressing it again takes it off — which is the only way back to an
            // unmarked part without closing the phase.
            const why = !place ? 'notice.3d.fitNoPlace'
              : !fitOf(h) ? 'notice.3d.fitNoMesh'
                : !h.through && holeDepth(h).mm == null ? 'notice.3d.fitNoDepth'
                  : null;
            const fitting = fitShown.has(h.id) && !why;
            // A POINTED SCREW'S POSITION (Louis 2026-10-29): kept and said, never drilled.
            const ownHole = h.via != null && makesOwnHole(items[Number(h.via)]);
            return (
              <tr
                key={h.id}
                onClick={() => place && flip(setMarked, h.id)}
                aria-pressed={on}
                // LIT IN ITS HOLE'S OWN COLOUR, so the row and the bore it put on the part read as
                // one thing — a light wash of it, the text staying legible.
                style={on ? { backgroundColor: `${tint(holeHue.get(h.id) ?? SAID_COLOURS[0])}26` } : undefined}
                className={`transition-colors ${switchable ? 'cursor-pointer' : ''} ${
                  !on && switchable ? 'hover:bg-slate-50' : ''}`}
              >
                <td className={`${td} tabular-nums font-semibold`}>
                  {shownHoleId(h.id, ownHole)}
                  {ownHole && <div className="text-[10px] font-normal italic text-slate-500">{st('notice.holes.ownHole')}</div>}
                </td>
                {only == null && (
                  <td className={td}>
                    {/* A bore answered against an assembly rather than a plank: kept and said,
                        so it can be argued with — nothing drills it (see placeInPart). */}
                    {onPart
                      ? <span className="whitespace-nowrap">{label(h.part, h.side)}</span>
                      : <span className="text-amber-600">{st('notice.holes.noPart')}</span>}
                  </td>
                )}
                <td className={`${td} tabular-nums`}>{at.join(', ')}</td>
                <td className={td}>{faceWord(h.face)}</td>
                <td className={td}>
                  <div className="whitespace-nowrap">{axis(h.u)}</div>
                  <div className="whitespace-nowrap">{axis(h.v)}</div>
                </td>
                <td className={`${td} whitespace-nowrap tabular-nums`}>
                  {[width != null ? `${t('notice.holes.diameter')} ${width} mm` : null, depth,
                    h.sunkMm ? t('notice.holes.sunk').replace('{n}', String(h.sunkMm)) : null,
                  ].filter(Boolean).join(' · ') || '—'}
                </td>
                <td className={td}>
                  {h.via ? <span className="whitespace-nowrap">{label(h.via)}</span> : '—'}
                </td>
                {only != null && (
                  <td className={`${td} whitespace-nowrap`}>
                    {/* THE HARDWARE, SLID INTO THIS HOLE on the part — see holeFit. Grey with
                        the reason where there is nothing to show: no place on this part, no
                        mesh for the hardware, or a blind hole with no depth. */}
                    <button
                      type="button"
                      // THE PRESS STOPS HERE. The row under it is a switch, and a press that
                      // reached both would mark the bore and cut it in one gesture — the second
                      // of which is not undoable by pressing again.
                      onClick={(e) => { e.stopPropagation(); flip(setFitShown, h.id); }}
                      disabled={!!why}
                      aria-pressed={fitting}
                      title={t(why ?? 'notice.3d.fitHint')}
                      className={`mr-1.5 ${PLAIN} ${fitting
                        // SOLID, not the row's own tint: a picked row is bg-blue-50 too, and the
                        // switch drawn in it vanished into it.
                        ? 'border-blue-600 bg-blue-600 text-white hover:bg-blue-500 hover:text-white'
                        : ''}`}
                    >
                      <Nut className="h-3 w-3" />
                      {st('notice.3d.fitShow')}
                    </button>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); if (cut) void drillRow(cut); }}
                      disabled={!cut || !canCut}
                      title={
                        done ? t('notice.3d.drillOneDoneHint')
                          : wrongHand ? t('notice.3d.drillSaidHand')
                            : !mmPerUnit ? t('notice.3d.holesNoScale')
                              : cut ? t('notice.3d.drillOneHint')
                                : t('notice.3d.drillOneNo')
                      }
                      className={PLAIN}
                    >
                      {boringOne === h.id
                        ? <Loader2 className="h-3 w-3 animate-spin" />
                        : done
                          ? <Check className="h-3 w-3" />
                          : <Drill className="h-3 w-3" />}
                      {/* AND IT SAYS WHICH KIND OF NOT-PRESSABLE IT IS: the commonest reason by
                          far is that the hole is already in the part. */}
                      {st(done ? 'notice.3d.drillOneDone' : 'notice.3d.drillOne')}
                    </button>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
      {/* THE TWO CONTROLS OF THE TABLE, under the list they are about: marking, and then
          cutting. Each says how many it will do, because that is rarely the number of rows — a
          hole with no width or no depth cannot be cut at all, and a button that quietly did
          eleven of fourteen would be one that had to be counted afterwards. */}
      {only != null && (
        <div className="mt-2 flex flex-col items-center gap-1.5">
          <div className="flex flex-wrap items-center justify-center gap-1.5">
            <PickAllButton
              all={allMarked}
              count={placeable.length}
              onPick={(on) => setMarked(on ? new Set(placeable) : new Set())}
            />
            <button
              type="button"
              onClick={() => void drillAll()}
              disabled={!drillable.length || !canCut}
              title={
                wrongHand ? t('notice.3d.drillSaidHand')
                  : mmPerUnit ? t('notice.3d.drillSaidHint')
                    : t('notice.3d.holesNoScale')
              }
              className={PLAIN}
            >
              {boring
                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : <Drill className="h-3.5 w-3.5" />}
              {t('notice.3d.drillSaid')}
              <span className="tabular-nums">
                {boring ? `· ${boring.done}/${boring.total}` : `· ${drillable.length}`}
              </span>
            </button>
          </div>
          {skipped.map((line) => (
            <span key={line} className="text-center text-[11px] text-slate-800">
              {line}
            </span>
          ))}
          {problem && (
            <p className="text-center text-xs text-rose-600">{problem}</p>
          )}
        </div>
      )}
    </TableCard>
  );
}
